import React, { ReactNode, useCallback, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { ActionButtonConfirmStatus } from "components";
import { useTypedSelector } from "store";
import { deleteProductLineAction } from "store/reducers/products/products.actions";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { ProcessState } from "types/process";
import { Alert, DialogContentText, Stack } from "@mui/material";
import { toast } from "react-toastify";

export default function DeleteProductLineButton({
  disabled = false,
}: {
  disabled?: boolean;
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const [processState, setProcessState] = useState<ProcessState>(
    ProcessState.IDLE
  );
  const [statusMessage, setStatusMessage] = useState<string>("");

  const productLine = useTypedSelector(
    (state) => state.products.selectedProductLine
  );
  const productLineId: string = productLine?.id ?? "";
  const productlineName: string = productLine?.name ?? "";

  //Disable the button if there is no productline
  const realDisabled: boolean = !!productLineId && disabled;

  const resetDialogState = useCallback(() => {
    setProcessState(ProcessState.IDLE);
    setStatusMessage("");
  }, []);

  const handleDeleteProductLine = async () => {
    try {
      if (productLineId) {
        setProcessState(ProcessState.PROCESSING);
        setStatusMessage("Creating Standard PVAs");

        dispatch(deleteProductLineAction(productLineId, history));

        const msg = `Product Line '${productlineName}' successfully deleted.`;
        toast.success(msg);
        setProcessState(ProcessState.COMPLETE);
        setStatusMessage(msg);
      }
    } catch (error) {
      const msg = `Error deleting Product Line '${productlineName}': ${error}`;
      console.error(msg);
      toast.error(msg);
      setProcessState(ProcessState.ERROR);
      setStatusMessage(msg);
    }
  };

  // Build the content section for the dialog
  const contentSection = useMemo<ReactNode>(() => {
    return (
      <Stack>
        <DialogContentText>
          Are you sure you want to delete the Product Line:
          <strong>{` '${productlineName}'`}</strong> ?
        </DialogContentText>

        <Alert variant="outlined" severity="warning" sx={{ mt: 1 }}>
          <strong>
            This will delete all Product Line data and cannot be undone!!!!
          </strong>
        </Alert>
      </Stack>
    );
  }, []);

  // Build the result section for the dialog
  const resultSection = undefined;

  return (
    <ActionButtonConfirmStatus
      // Button Props
      variant="outlined"
      color="warning"
      disabled={realDisabled}
      size="medium"
      // Icon Props
      icon={DeleteIcon}
      iconOnly={false}
      // Tooltip Props
      tooltipText={"Delete the Product Line"}
      tooltipArrow={true}
      tooltipPlacement="right"
      // Dialog Props
      maxWidth="sm"
      dialogTitle={"Delete Product Line"}
      onDialogOpen={resetDialogState}
      disableEscapeKeyDown={true}
      disableCloseOnBackdropClick={true}
      // Content Section Props
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      // Action Button Props
      defaultButton="CANCEL"
      cancelButtonText="Cancel"
      okButtonText="OK"
      okButtonWarningColor
      onOKButtonClick={handleDeleteProductLine}
      // Status Section Props
      processState={processState}
      statusMessage={statusMessage}
      // Result Section Props
      resultSection={resultSection}
    >
      Delete Product Line
    </ActionButtonConfirmStatus>
  );
}
